import React, { useState } from "react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import "./NewPatientRegistration.css";

const theme = {
  primaryDark: "#0f172a", primaryBlue: "#1e40af", accentCyan: "#0ea5e9",
  bgLight: "#f8fafc", cardBg: "#ffffff", textMain: "#334155",
  textMuted: "#64748b", borderLight: "#e2e8f0", successDark: "#15803d",
  dangerDark: "#b91c1c", warningDark: "#a16207"
};

const IndentReport = () => {
  // Start with completely empty state
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // 🔥 FIX: Track if the user has clicked the button yet
  const [hasSearched, setHasSearched] = useState(false);

  const fetchReport = async () => {
    // If the user wants ALL records, they can leave dates empty, but they MUST click the button.
    const queryStart = startDate || "2000-01-01";
    const queryEnd = endDate || "2100-12-31";

    if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
      return alert("Start date cannot be after End date.");
    }

    setLoading(true);
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      const res = await fetch(`https://hmsapi.emedhatechnologies.com/master/api/api/reports/medicine-indents?start_date=${queryStart}&end_date=${queryEnd}&employee_id=${user?.id}`);
      const data = await res.json();
      if (data.success) {
        setReportData(data.data);
        setHasSearched(true); // Mark that a search has been completed
      } else {
        alert(data.message || "Failed to fetch report");
      }
    } catch (err) {
      console.error("Report Fetch Error:", err);
      alert("Network error occurred.");
    }
    setLoading(false);
  };

  const handleClearFilters = () => {
    // 🔥 FIX: Clear everything and return to the initial empty state
    setStartDate("");
    setEndDate("");
    setReportData([]);
    setHasSearched(false);
  };

  // --- EXPORT TO EXCEL (CSV) ---
  const exportToExcel = () => {
    if (reportData.length === 0) return alert("No data to export!");

    const headers = [
      "Indent No", "Date", "Location", "Supplier", "Status", 
      "Medicine ID", "Medicine Name", "Unit", "Purchase Rate", 
      "Indent Qty", "Ordered Qty", "Estimated Value"
    ];

    const csvRows = reportData.map(row => [
      row.indent_number,
      row.indent_date,
      `"${row.location_name}"`, 
      `"${row.supplier_name}"`,
      row.indent_status,
      row.medicine_id,
      `"${row.medicine_name}"`,
      row.unit,
      row.purchase_rate,
      row.indent_qty,
      row.ordered_qty,
      row.estimated_value
    ].join(","));

    const csvString = [headers.join(","), ...csvRows].join("\n");
    const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    
    const link = document.createElement("a");
    link.href = url;
    const fileNameDate = startDate && endDate ? `${startDate}_to_${endDate}` : "All_Records";
    link.setAttribute("download", `Indent_Report_${fileNameDate}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // --- DIRECT PDF DOWNLOAD (AutoTable) ---
  const exportToPDF = () => {
    if (reportData.length === 0) return alert("No data to print!");

    const doc = new jsPDF("landscape");

    doc.setFontSize(16);
    doc.setTextColor(30, 64, 175);
    doc.text("EMEDHA CARE HOSPITAL - Medicine Indent Report", 14, 15);
    
    doc.setFontSize(10);
    doc.setTextColor(100, 116, 139);
    const displayRange = startDate && endDate ? `${startDate} to ${endDate}` : "All Time";
    doc.text(`Date Range: ${displayRange}  |  Generated On: ${new Date().toLocaleString()}`, 14, 22);

    const tableColumn = ["Indent No", "Date", "Location (OT/Ward)", "Supplier", "Status", "Medicine Name", "Req Qty", "Ord Qty", "Est. Value"];
    const tableRows = [];

    reportData.forEach(row => {
      const rowData = [
        row.indent_number,
        row.indent_date,
        row.location_name,
        row.supplier_name,
        row.indent_status,
        `${row.medicine_name}\n(ID: ${row.medicine_id})`,
        row.indent_qty,
        row.ordered_qty,
        `Rs. ${parseFloat(row.estimated_value).toFixed(2)}`
      ];
      tableRows.push(rowData);
    });

    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: 28,
      styles: { fontSize: 8, cellPadding: 3 },
      headStyles: { fillColor: [30, 64, 175], textColor: 255, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      columnStyles: {
        8: { halign: 'right', fontStyle: 'bold', textColor: [30, 64, 175] }
      }
    });

    const fileNameDate = startDate && endDate ? `${startDate}_to_${endDate}` : "All_Records";
    doc.save(`Indent_Report_${fileNameDate}.pdf`);
  };

  return (
    <div className="registration-container fade-in" style={{ backgroundColor: theme.bgLight, minHeight: '100vh', padding: '20px 2%', fontFamily: "'Inter', 'Segoe UI', sans-serif" }}>
      
      <style>{`
        /* --- BRAND NEW HEADER UI --- */
        .report-header-container { background: #fff; padding: 20px 25px; border-radius: 12px; border: 1px solid ${theme.borderLight}; box-shadow: 0 4px 15px rgba(0,0,0,0.03); margin-bottom: 25px; }
        .report-title { margin: 0 0 15px 0; color: ${theme.primaryDark}; font-size: 22px; font-weight: 800; }
        
        .report-toolbar { display: flex; flex-wrap: wrap; justify-content: space-between; align-items: center; gap: 20px; }
        
        .filter-group { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
        .date-input-wrap { display: flex; align-items: center; gap: 8px; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 6px 12px; height: 42px; box-sizing: border-box; }
        .date-input-wrap label { font-size: 12px; font-weight: 700; color: #64748b; }
        .date-input-wrap input { border: none; background: transparent; outline: none; color: #334155; font-weight: 600; font-family: inherit; font-size: 13px; }
        
        .action-group { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }

        /* --- BUTTONS --- */
        .btn-base { display: flex; align-items: center; justify-content: center; gap: 6px; padding: 0 16px; border: none; border-radius: 8px; font-weight: 700; cursor: pointer; transition: 0.2s; font-size: 13px; height: 42px; white-space: nowrap; box-sizing: border-box; }
        .btn-filter { background: ${theme.primaryBlue}; color: white; }
        .btn-filter:hover { background: #1e3a8a; box-shadow: 0 4px 10px rgba(30, 64, 175, 0.2); }
        .btn-clear { background: #f1f5f9; color: #475569; border: 1px solid #cbd5e1; }
        .btn-clear:hover { background: #e2e8f0; color: #0f172a; }
        .btn-pdf { background: #fee2e2; color: #b91c1c; border: 1px solid #fecaca; }
        .btn-pdf:hover { background: #fecaca; }
        .btn-excel { background: #dcfce7; color: #15803d; border: 1px solid #bbf7d0; }
        .btn-excel:hover { background: #bbf7d0; }

        /* --- GRID --- */
        .erp-grid { width: 100%; border-collapse: collapse; background: #fff; font-size: 13px; }
        .erp-grid th { background: #f1f5f9; color: ${theme.primaryDark}; padding: 14px 15px; text-align: left; font-weight: 700; border-bottom: 2px solid ${theme.borderLight}; white-space: nowrap; text-transform: uppercase; font-size: 12px; }
        .erp-grid td { padding: 12px 15px; border-bottom: 1px solid ${theme.borderLight}; color: ${theme.textMain}; white-space: nowrap; }
        .erp-grid tr:hover { background-color: #f8fafc; }
        .table-wrapper { background: #fff; border-radius: 12px; border: 1px solid ${theme.borderLight}; overflow-x: auto; box-shadow: 0 4px 15px rgba(0,0,0,0.03); }
        
        .clean-scrollbar::-webkit-scrollbar { height: 8px; }
        .clean-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
      `}</style>

      <div style={{ maxWidth: '1600px', margin: '0 auto', display: 'flex', flexDirection: 'column' }}>
        
        {/* HEADER TOOLBAR */}
        <div className="report-header-container">
          <h2 className="report-title">Medicine Indent Report</h2>
          
          <div className="report-toolbar">
            
            {/* Filter Section (Left) */}
            <div className="filter-group">
              <div className="date-input-wrap">
                <label>From</label>
                <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
              </div>
              <div className="date-input-wrap">
                <label>To</label>
                <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
              </div>
              <button className="btn-base btn-filter" onClick={fetchReport}>🔍 Generate Data</button>
              {hasSearched && (
                <button className="btn-base btn-clear" onClick={handleClearFilters}>✖ Clear</button>
              )}
            </div>

            {/* Actions Section (Right) */}
            <div className="action-group">
              <button className="btn-base btn-pdf" onClick={exportToPDF} disabled={loading || reportData.length === 0} style={{ opacity: reportData.length === 0 ? 0.5 : 1 }}>
                📄 Download PDF
              </button>
              <button className="btn-base btn-excel" onClick={exportToExcel} disabled={loading || reportData.length === 0} style={{ opacity: reportData.length === 0 ? 0.5 : 1 }}>
                📊 Download Excel
              </button>
            </div>

          </div>
        </div>

        {/* DATA GRID */}
        {loading ? (
          <div style={{ padding: '50px', textAlign: 'center', color: theme.accentCyan, fontWeight: '700', fontSize: '16px' }}>Fetching Records...</div>
        ) : (
          <div className="table-wrapper clean-scrollbar">
            <table className="erp-grid">
              <thead>
                <tr>
                  <th>Indent No</th>
                  <th>Date</th>
                  <th>Location (Ward/OT)</th>
                  <th>Supplier</th>
                  <th>Status</th>
                  <th>Medicine</th>
                  <th>Indent Qty</th>
                  <th>Ordered Qty</th>
                  <th>Est. Value</th>
                </tr>
              </thead>
              <tbody>
                {reportData.map((row, idx) => (
                  <tr key={idx}>
                    <td style={{ fontWeight: '800', color: theme.primaryBlue }}>{row.indent_number}</td>
                    <td>{row.indent_date}</td>
                    <td>{row.location_name}</td>
                    <td>{row.supplier_name}</td>
                    <td>
                      <span style={{ 
                        backgroundColor: row.indent_status.includes('Created') ? theme.bgLight : row.indent_status.includes('Partially') ? '#fefce8' : '#dcfce7',
                        color: row.indent_status.includes('Created') ? theme.textMuted : row.indent_status.includes('Partially') ? '#a16207' : '#15803d',
                        padding: '4px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: '700', border: '1px solid currentColor'
                      }}>
                        {row.indent_status}
                      </span>
                    </td>
                    <td>
                      <span style={{ fontWeight: '700' }}>{row.medicine_name}</span><br/>
                      <span style={{ fontSize: '10px', color: theme.textMuted }}>ID: {row.medicine_id} | Unit: {row.unit} | Rate: ₹{row.purchase_rate}</span>
                    </td>
                    <td style={{ fontWeight: '700' }}>{row.indent_qty}</td>
                    <td style={{ fontWeight: '700', color: theme.successDark }}>{row.ordered_qty}</td>
                    <td style={{ fontWeight: '800', color: theme.primaryDark, textAlign: 'right' }}>₹{parseFloat(row.estimated_value).toFixed(2)}</td>
                  </tr>
                ))}

                {/* 🔥 FIX: Empty State Messaging Logic */}
                {reportData.length === 0 && !hasSearched && (
                  <tr>
                    <td colSpan="9" style={{ textAlign: 'center', padding: '60px 40px', color: theme.textMuted, fontSize: '14px', fontWeight: '600' }}>
                      <div style={{ fontSize: '32px', marginBottom: '10px' }}>📅</div>
                      Please select a date range and click "Generate Data" to view the report.
                    </td>
                  </tr>
                )}

                {reportData.length === 0 && hasSearched && (
                  <tr>
                    <td colSpan="9" style={{ textAlign: 'center', padding: '60px 40px', color: theme.textMuted, fontSize: '14px', fontWeight: '600' }}>
                      <div style={{ fontSize: '32px', marginBottom: '10px' }}>📂</div>
                      No indent records found. Adjust your filters to see more data.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

      </div>
    </div>
  );
};

export default IndentReport;

